import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';

const MarkupContainer = styled(Box)(({ theme }) => ({
  '& strong, & b': {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  '& em, & i': {
    fontStyle: 'italic',
  },
  '& code': {
    backgroundColor: theme.palette.grey[100],
    padding: '2px 4px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  },
  '& ul, & ol': {
    margin: 0,
    paddingLeft: theme.spacing(2),
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  '& p': {
    margin: 0,
    '&:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },
  },
}));

interface ProgressDetailsMarkupMAIQProps {
  content: string;
  variant?: 'default' | 'bold' | 'large';
}

export const ProgressDetailsMarkupMAIQ: React.FC<ProgressDetailsMarkupMAIQProps> = ({ 
  content, 
  variant = 'default' 
}) => {
  const parseMarkup = (text: string) => {
    // Handle **bold** text
    let parsed = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Handle *italic* text
    parsed = parsed.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Handle bullet points
    parsed = parsed.replace(/^•\s*(.*)$/gm, '<li>$1</li>');
    
    // Wrap consecutive list items in ul
    parsed = parsed.replace(/(<li>.*<\/li>(\s*<li>.*<\/li>)*)/g, '<ul>$1</ul>');
    
    // Handle line breaks
    parsed = parsed.replace(/\n/g, '<br />');
    
    return parsed;
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'bold':
        return {
          fontSize: '1rem',
          fontWeight: 600,
        };
      case 'large':
        return {
          fontSize: '2.5rem',
          fontWeight: 700,
          lineHeight: 1,
        };
      default:
        return {
          fontSize: '1rem',
          fontWeight: 500,
          lineHeight: 1.4,
        };
    }
  };

  if (!content) return null;

  return (
    <MarkupContainer>
      <Typography
        component="div"
        sx={{
          color: 'text.primary',
          ...getVariantStyles(),
        }}
        dangerouslySetInnerHTML={{ __html: parseMarkup(content) }}
      />
    </MarkupContainer>
  );
};
